import React, { useState } from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send, 
  CheckCircle,
  AlertCircle,

} from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    urgency: 'normal'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      // For development, use the full URL with port 8000 for the backend
      const apiUrl = import.meta.env.DEV 
        ? 'http://localhost:8000' 
        : import.meta.env.VITE_API_URL || 'http://localhost:8000';
      
      console.log('Submitting to:', `${apiUrl}/api/contact`);
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitStatus('success');
        // Reset form on successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: '',
          urgency: 'normal'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Phone Numbers",
      details: [
        "+256 393 254458",
        "+256 740 825445"
      ],
      link: "tel:+256740825445",
      description: "24/7 Emergency Response Available"
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email Address",
      details: [
        "info@biohazwastesolutions.com"
      ],
      link: "mailto:info@biohazwastesolutions.com",
      description: "Response within 2 hours"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Office Location",
      details: [
        "Plot 27, Valley Drive",
        "Minister's Village, Ntinda",
        "Kampala, Uganda"
      ],
      link: "https://maps.google.com/?q=Plot+27+Valley+Drive+Ntinda+Kampala+Uganda",
      description: "Visit us during business hours"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Business Hours",
      details: [
        "Monday - Friday: 8:00 AM - 6:00 PM",
        "Saturday: 9:00 AM - 4:00 PM",
        "Sunday: Emergency Only"
      ],
      link: null,
      description: "Emergency services available 24/7"
    }
  ];

  const services = [
    "Medical & Hazardous Waste Disposal",
    "Transportation & Logistics",
    "Incineration Services",
    "Plastic Shredding",
    "Hoarding Cleanup",
    "Training and Consultancy",
    "e-Waste Management",
    "Expired Item Disposal",
    "Textile Waste Treatment",
    "Bottle Sterilization",
    "Other (Please specify in message)"
  ];

  return (
    <div className="w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-14 sm:py-16 md:py-20 xl:py-24 bg-gradient-to-br from-[#295cab] to-[#1e4a8a] text-white w-full px-2 sm:px-4 md:px-8">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12 text-center w-full">
          <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-bold mb-6">Contact Us</h1>
          <p className="text-base sm:text-lg md:text-xl xl:text-2xl max-w-3xl mx-auto leading-relaxed">
            Ready to partner with Uganda's leading waste management company? Get in touch today for professional solutions.
          </p>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="bg-red-600 text-white py-3 sm:py-4 w-full px-2 sm:px-4 md:px-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
            <AlertCircle className="w-6 h-6" />
            <span className="font-semibold">EMERGENCY HOTLINES: </span>
            <div className="flex flex-col sm:flex-row gap-2">
              <a href="tel:+256393254458" className="hover:underline font-bold">
                +256 393 254458
              </a>
              <span className="hidden sm:inline">|</span>
              <a href="tel:+256740825445" className="hover:underline font-bold">
                +256 740 825445
              </a>
            </div>
            <span className="text-sm">Available 24/7 for urgent waste management needs</span>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-base sm:text-lg md:text-xl xl:text-2xl text-gray-600 max-w-2xl mx-auto">
              Multiple ways to reach our team for all your waste management needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 md:gap-10">
            {contactInfo.map((info, index) => (
              <div key={index} className="text-center p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl hover:shadow-lg transition-all duration-300">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 text-[#295cab] rounded-full mb-6">
                  {info.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{info.title}</h3>
                <div className="space-y-1 mb-4">
                  {info.details.map((detail, detailIndex) => (
                    <p key={detailIndex} className="text-gray-700">
                      {info.link && detailIndex === 0 ? (
                        <a href={info.link} className="hover:text-[#295cab] transition-colors">
                          {detail}
                        </a>
                      ) : (
                        detail
                      )}
                    </p>
                  ))}
                </div>
                <p className="text-sm text-gray-600">{info.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-12 sm:py-16 md:py-20 xl:py-24 bg-gray-50 w-full px-2 sm:px-4 md:px-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-gray-900 mb-6">Request a Service</h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8">
                Fill out the form below and our team will respond within 2 hours during business hours. 
                For emergency services, please call our 24/7 hotline.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-50 text-[#295cab] rounded-full flex items-center justify-center mt-1">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Free Consultation</h4>
                    <p className="text-gray-600">Initial assessment and quote at no cost</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-50 text-[#295cab] rounded-full flex items-center justify-center mt-1">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Tailored Solutions</h4>
                    <p className="text-gray-600">Custom waste management plans for your needs</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-50 text-[#295cab] rounded-full flex items-center justify-center mt-1">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Fast Response</h4>
                    <p className="text-gray-600">Quick turnaround on all service requests</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg">
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-[#295cab]" />
                    <span className="text-[#1e4a8a] font-medium">Thank you! We'll contact you soon.</span>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#295cab] focus:border-[#295cab] transition-colors text-base sm:text-lg min-h-[48px]"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#295cab] focus:border-[#295cab] transition-colors text-base sm:text-lg min-h-[48px]"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#295cab] focus:border-[#295cab] transition-colors text-base sm:text-lg min-h-[48px]"
                      placeholder="+256 xxx xxx xxx"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      Company/Organization
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#295cab] focus:border-[#295cab] transition-colors text-base sm:text-lg min-h-[48px]"
                      placeholder="Enter company name"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                      Service Needed *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#295cab] focus:border-[#295cab] transition-colors text-base sm:text-lg min-h-[48px]"
                    >
                      <option value="">Select a service</option>
                      {services.map((service, index) => (
                        <option key={index} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-2">
                      Urgency Level
                    </label>
                    <select
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#295cab] focus:border-[#295cab] transition-colors text-base sm:text-lg min-h-[48px]"
                    >
                      <option value="normal">Normal (2-3 days)</option>
                      <option value="urgent">Urgent (24 hours)</option>
                      <option value="emergency">Emergency (Immediate)</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-base sm:text-lg min-h-[48px]"
                    placeholder="Please describe your waste management needs, including type of waste, volume, frequency, and any special requirements..."
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#295cab] text-white px-6 py-4 rounded-lg font-semibold hover:bg-[#1e4a8a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 min-h-12 text-base sm:text-lg"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Send Request</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-gray-900 mb-4">Find Us</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">
              Visit our office in Ntinda, Kampala for in-person consultations
            </p>
          </div>
          
          <div className="bg-gray-200 rounded-xl overflow-x-auto max-w-full" style={{ height: '400px' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3989.746985937828!2d32.62892531431426!3d0.35463826397989816!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x177dba5b1b4e1c65%3A0x4e45e5b8a0d4c4c8!2sNtinda%2C%20Kampala%2C%20Uganda!5e0!3m2!1sen!2s!4v1640123456789!5m2!1sen!2s"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="BioHaz Waste Solutions Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;